import { Button, InputAdornment, Stack } from '@mui/material'
import { AuthError } from 'firebase/auth'
import React, { forwardRef, useState } from 'react'
import { IconButton, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { tLabel, tMessage, useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, SubmitHandler, useForm, UseFormProps } from '@/core/form'

export type PasswordSettingFormProps = {
  onSubmit: SubmitHandler<PasswordFormValues>
}

export type PasswordFormValues = {
  password: string
  newPassword: string
  confirmNewPassword: string
}

const schema: UseFormProps['schema'] = o => ({
  password: o.string().min(8).label(tLabel('passwordSettings.password')),
  newPassword: o.string().min(8).label(tLabel('passwordSettings.newPassword')),
  confirmNewPassword: o
    .string()
    .min(8)
    .valid(o.ref('newPassword'))
    .messages({
      'any.only': tMessage('passwordSetting.confirmNewPassword.valid'),
    })
    .label(tLabel('passwordSettings.confirmNewPassword')),
})

const defaultValues: PasswordFormValues = {
  password: '',
  newPassword: '',
  confirmNewPassword: '',
}

export const PasswordSettingForm: Organism<PasswordSettingFormProps> = ({
  onSubmit,
}) => {
  const { t } = useTranslation(['passwordSettings', 'firebase'])
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<PasswordFormValues>({
    schema,
    mode: 'onChange',
    defaultValues,
  })
  const { showMessage, showError } = useMessage()

  return (
    <Form
      onSubmit={handleSubmit(
        inputs =>
          onSubmit(inputs)
            .then(() => {
              showMessage(t('passwordSettings:registerSuccess'), 'success')
              reset()
            })
            .catch((e: AuthError) =>
              showError(new Error(t(`firebase:auth.errors.${e.code}`)))
            ),
        e => {
          showError(new Error(t('emailAddressSettings:error')))
          throw e
        }
      )}
    >
      <PasswordInputCell error={errors?.password} {...register('password')} />

      <PasswordInputCell
        error={errors?.newPassword}
        {...register('newPassword')}
      />

      <PasswordInputCell
        error={errors?.confirmNewPassword}
        {...register('confirmNewPassword')}
      />

      <ButtonCell loading={isSubmitting} />
    </Form>
  )
}

const PasswordInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't' | 'type'>, ref) => {
    const { t } = useTranslation('passwordSettings')
    const [showPassword, setShowPassword] = useState(false)

    return (
      <CellFrame>
        <TextInputField
          type={showPassword ? 'text' : 'password'}
          {...props}
          textFieldProps={{
            ...props.textFieldProps,
            InputProps: {
              ...props.textFieldProps?.InputProps,
              inputProps: { variant: 'body2', sx: { px: 4, py: 3 } },
              endAdornment: (
                <InputAdornment position="end">
                  {showPassword ? (
                    <IconButton.Display
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  ) : (
                    <IconButton.Hide
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  )}
                </InputAdornment>
              ),
            },
          }}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('passwordSettings')
  const { router } = useRouter()

  return (
    <CellFrame sx={{ px: 4, py: 3, textAlign: 'center' }}>
      <Stack spacing={1} alignItems="center">
        <Button
          type="submit"
          size="large"
          sx={{ width: 244 }}
          loading={loading}
        >
          {t('submit')}
        </Button>
        <Button
          variant="text"
          size="large"
          sx={{ width: 244 }}
          onClick={() => router.goBack()}
        >
          {t('back')}
        </Button>
      </Stack>
    </CellFrame>
  )
}
